/**
 * Mobile - Handles mobile-specific UI functionality
 * 
 * Required from parent (voiceInterface):
 * - isMobile: boolean
 * - messages: Array (mobile message history)
 * - t(key): string (translation helper)
 * - shadowRoot: ShadowRoot | Document
 * 
 * State owned by this module:
 * - waveformBars: Array<number> - Waveform animation state
 * - waveformInterval: number | null - Waveform animation interval
 * - isTranscriptExpanded: boolean - Mobile transcript expansion state
 */

export class Mobile {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
    // State owned by this module
    this.waveformBars = [0.3, 0.5, 0.8, 0.4, 0.6, 0.9, 0.5, 0.7];
    this.waveformInterval = null;
    this.isTranscriptExpanded = false;
    // Initialize messages array on parent (owned by Mobile, accessed via voiceInterface.messages)
    if (!this.voiceInterface.messages) {
      this.voiceInterface.messages = [];
    }
  }

  /**
   * Helper to access translation method from parent
   */
  t(key) {
    return this.voiceInterface.t(key);
  }

  /**
   * Create mobile minimized bar (appends to document.body)
   */
  createMobileMinimizedBar() {
    // Remove existing bar if any
    const existingBar = document.getElementById('mobile-voice-call-bar-container');
    if (existingBar) {
      existingBar.remove();
    }
    
    // Inject mobile CSS into document head if not already injected
    if (!document.getElementById('mobile-voice-call-bar-styles')) {
      const style = document.createElement('style');
      style.id = 'mobile-voice-call-bar-styles';
      style.textContent = this.voiceInterface.styles.generateMobileCSS();
      document.head.appendChild(style);
    }
    
    // Create container div
    const container = document.createElement('div');
    container.id = 'mobile-voice-call-bar-container';
    container.innerHTML = `
      <!-- Duration Badge -->
      <div class="mobile-duration-badge" id="mobileDurationBadge">
        <div class="mobile-duration-dot"></div>
        <span id="mobileDurationText">00:00</span>
      </div>
      
      <!-- Main Bar -->
      <div class="mobile-voice-bar">
        <!-- Top Row: Status, Waveform, Controls -->
        <div class="mobile-top-row">
          <!-- Status Indicator -->
          <div class="mobile-status-indicator">
            <div class="mobile-status-dot" id="mobileStatusDot"></div>
            <span class="mobile-status-text" id="mobileStatusText">${this.t('listening')}</span>
          </div>
          
          <!-- Centered Waveform -->
          <div class="mobile-waveform-center" id="mobileWaveform">
            ${Array(8).fill(0).map((_, i) => 
              `<div class="mobile-waveform-bar" data-index="${i}"></div>`
            ).join('')}
          </div>
          
          <!-- Controls -->
          <div class="mobile-controls">
            <button class="mobile-control-btn" id="mobileMuteBtn" aria-label="Mute microphone">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-mic-icon">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" x2="12" y1="19" y2="22"/>
              </svg>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-mic-off-icon" style="display: none;">
                <line x1="2" x2="22" y1="2" y2="22"/>
                <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"/>
                <path d="M5 10v2a7 7 0 0 0 12 5"/>
                <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/>
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12"/>
                <line x1="12" x2="12" y1="19" y2="22"/>
              </svg>
            </button>
            
            <button class="mobile-control-btn" id="mobileSpeakerBtn" aria-label="Toggle speaker">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-speaker-icon">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-speaker-off-icon" style="display: none;">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <line x1="22" x2="16" y1="9" y2="15"/>
                <line x1="16" x2="22" y1="9" y2="15"/>
              </svg>
            </button>
            
            <button class="mobile-end-call-btn" id="mobileEndCallBtn" aria-label="End call">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>
                <line x1="22" x2="2" y1="2" y2="22"/>
              </svg>
            </button>
          </div>
        </div>
        
        <!-- Bottom Row: Transcript (expandable) -->
        <button class="mobile-transcript-row" id="mobileTranscriptRow">
          <p class="mobile-transcript-text" id="mobileTranscriptText">${this.t('transcriptWillAppear')}</p>
          <div class="mobile-transcript-footer">
            <span class="mobile-expand-hint">tap to expand & type</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.6;">
              <path d="M7 17L17 7M17 7H7M17 7V17"/>
            </svg>
          </div>
        </button>
      </div>
      
      <!-- Expanded Transcript Overlay -->
      <div class="mobile-transcript-overlay" id="mobileTranscriptOverlay" style="display: none;">
        <div class="mobile-expanded-transcript" id="mobileExpandedTranscript">
          <!-- Header -->
          <div class="mobile-transcript-header">
            <div class="mobile-header-left">
              <div class="mobile-header-status">
                <div class="mobile-header-status-dot"></div>
                <span class="mobile-header-status-text" id="mobileHeaderStatusText">${this.t('listening')}</span>
                <span class="mobile-header-duration" id="mobileHeaderDuration">00:00</span>
              </div>
              <span class="mobile-transcript-label">CONVERSATION</span>
            </div>
            <button class="mobile-close-transcript-btn" id="mobileCloseTranscriptBtn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          
          <!-- Messages Container -->
          <div class="mobile-messages-container" id="mobileMessagesContainer">
            <!-- Messages will be dynamically added here -->
          </div>
          
          <!-- Text Input Area -->
          <div class="mobile-input-area">
            <div class="mobile-input-wrapper">
              <input
                type="text"
                placeholder="Type a message..."
                id="mobileTextInput"
                class="mobile-text-input"
              />
              <button class="mobile-send-btn" id="mobileSendBtn" disabled>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
              </button>
            </div>
            <p class="mobile-input-hint">🎙️ ${this.t('voiceActive') || 'Voice is still active'} • ${this.t('typeOrSpeak') || 'Type or speak'}</p>
          </div>
        </div>
      </div>
    `;
    
    // Append to body
    document.body.appendChild(container);
    
    // Apply inline styles to buttons to ensure they're circular (highest specificity)
    const muteBtn = document.getElementById('mobileMuteBtn');
    const speakerBtn = document.getElementById('mobileSpeakerBtn');
    const endCallBtn = document.getElementById('mobileEndCallBtn');
    
    if (muteBtn) {
      muteBtn.style.cssText = `
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        max-width: 32px !important;
        max-height: 32px !important;
        aspect-ratio: 1 !important;
        border-radius: 50% !important;
        overflow: hidden !important;
        padding: 0 !important;
        margin: 0 !important;
        box-sizing: border-box !important;
      `;
      // Style the visible (white) mic icon - show it, hide the red one
      const micIcon = muteBtn.querySelector('.mobile-mic-icon');
      const micOffIcon = muteBtn.querySelector('.mobile-mic-off-icon');
      
      if (micIcon) {
        micIcon.style.cssText = `
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          width: 15px !important;
          height: 15px !important;
          margin: 0 !important;
          padding: 0 !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        `;
      }
      // Hide the muted (red) mic icon
      if (micOffIcon) {
        micOffIcon.style.cssText = `
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        `;
      }
    }
    
    if (speakerBtn) {
      speakerBtn.style.cssText = `
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        min-height: 32px !important;
        max-width: 32px !important;
        max-height: 32px !important;
        aspect-ratio: 1 !important;
        border-radius: 50% !important;
        overflow: hidden !important;
        padding: 0 !important;
        margin: 0 !important;
        box-sizing: border-box !important;
      `;
      // Style the visible (white) speaker icon - show it, hide the red one
      const speakerIcon = speakerBtn.querySelector('.mobile-speaker-icon');
      const speakerOffIcon = speakerBtn.querySelector('.mobile-speaker-off-icon');
      
      if (speakerIcon) {
        speakerIcon.style.cssText = `
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          width: 15px !important;
          height: 15px !important;
          margin: 0 !important;
          padding: 0 !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        `;
      }
      // Hide the muted (red) speaker icon
      if (speakerOffIcon) {
        speakerOffIcon.style.cssText = `
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
        `;
      }
    }
    
    if (endCallBtn) {
      endCallBtn.style.cssText = `
        width: 36px !important;
        height: 36px !important;
        min-width: 36px !important;
        min-height: 36px !important;
        max-width: 36px !important;
        max-height: 36px !important;
        aspect-ratio: 1 !important;
        border-radius: 50% !important;
        overflow: hidden !important;
        padding: 0 !important;
        margin: 0 !important;
        box-sizing: border-box !important;
      `;
      const endCallSvg = endCallBtn.querySelector('svg');
      if (endCallSvg) {
        endCallSvg.style.cssText = `
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          width: 15px !important;
          height: 15px !important;
          margin: 0 !important;
          padding: 0 !important;
        `;
      }
    }
    
    // Setup event handlers for the new elements
    this.voiceInterface.setupMobileEventHandlers();
  }

  /**
   * Remove mobile minimized bar
   */
  removeMobileMinimizedBar() {
    const container = document.getElementById('mobile-voice-call-bar-container');
    if (container) {
      container.remove();
    }
    // Keep CSS in head for potential reuse
  }

  /**
   * Expand mobile transcript overlay
   */
  expandMobileTranscript() {
    const overlay = document.getElementById('mobileTranscriptOverlay');
    if (overlay) {
      overlay.style.display = 'flex';
      this.isTranscriptExpanded = true;
      
      // Focus input after a short delay
      const input = document.getElementById('mobileTextInput');
      if (input) {
        setTimeout(() => input.focus(), 100);
      }
      
      // Scroll to bottom of messages
      const messagesContainer = document.getElementById('mobileMessagesContainer');
      if (messagesContainer) {
        setTimeout(() => {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }, 100);
      }
    }
  }

  /**
   * Collapse mobile transcript overlay
   */
  collapseMobileTranscript() {
    const overlay = document.getElementById('mobileTranscriptOverlay');
    if (overlay) {
      overlay.style.display = 'none';
      this.isTranscriptExpanded = false;
    }
  }

  /**
   * Update mobile status display
   */
  updateMobileStatus(status) {
    // Implementation will be moved from VoiceInterface
    const statusText = document.getElementById('mobileStatusText');
    const headerStatusText = document.getElementById('mobileHeaderStatusText');
    const statusDot = document.getElementById('mobileStatusDot');
    
    if (!statusText && !headerStatusText) return;
    
    // Use custom statusText from config if provided, otherwise use translations
    const customStatusText = this.config.statusText;
    let displayText = '';
    let dotColor = '#6b7280';
    
    if (customStatusText) {
      // Use custom status text for all states
      displayText = customStatusText;
      dotColor = this.config.statusDotColor || '#10b981';
    } else {
      // Use dynamic status based on state
      if (status === 'connecting') {
        displayText = this.t('connecting') || 'Connecting...';
        dotColor = '#3b82f6'; // Blue for connecting
      } else if (status === 'listening' || status === 'recording') {
        displayText = this.t('listening') || 'Listening...';
        dotColor = '#22c55e';
      } else if (status === 'speaking') {
        displayText = this.t('speaking') || 'Speaking...';
        dotColor = '#a855f7';
      } else {
        displayText = this.t('paused') || 'Paused';
        dotColor = '#facc15';
      }
    }
    
    if (statusText) statusText.textContent = displayText;
    if (headerStatusText) headerStatusText.textContent = displayText;
    if (statusDot) {
      statusDot.style.backgroundColor = dotColor;
      statusDot.style.boxShadow = status === 'paused' ? 'none' : `0 0 8px ${dotColor}`;
    }
  }

  /**
   * Add message to mobile UI
   */
  addMobileMessage(type, text) {
    // Use parent's messages array (owned by mobile but accessed via voiceInterface)
    this.voiceInterface.messages.push({ type, text });
    // Update UI
    const messagesContainer = document.getElementById('mobileMessagesContainer');
    if (messagesContainer) {
      // Render message
      const messageEl = document.createElement('div');
      messageEl.className = `mobile-message-bubble ${type}`;
      messageEl.textContent = text;
      messagesContainer.appendChild(messageEl);
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  /**
   * Start mobile waveform animation
   */
  startWaveformAnimation() {
    if (this.waveformInterval) {
      clearInterval(this.waveformInterval);
    }
    
    const waveform = document.getElementById('mobileWaveform');
    if (!waveform) return;
    
    this.waveformInterval = setInterval(() => {
      const bars = waveform.querySelectorAll('.mobile-waveform-bar');
      bars.forEach((bar, i) => {
        const height = Math.random() * 60 + 10;
        bar.style.height = `${height}px`;
      });
    }, 100);
  }

  /**
   * Stop mobile waveform animation
   */
  stopWaveformAnimation() {
    if (this.waveformInterval) {
      clearInterval(this.waveformInterval);
      this.waveformInterval = null;
    }

    const waveform = document.getElementById('mobileWaveform');
    if (waveform) {
      const bars = waveform.querySelectorAll('.mobile-waveform-bar');
      bars.forEach(bar => {
        bar.style.height = '10px';
      });
    }
  }

  /**
   * Cleanup all mobile intervals and resources
   */
  cleanup() {
    this.stopWaveformAnimation();
    this.collapseMobileTranscript();
    this.removeMobileMinimizedBar();
    // Clear messages array
    this.voiceInterface.messages = [];
  }
}
